import React, { useEffect, useState, useCallback } from 'react';

import jwtDecode from 'jwt-decode';
import PropTypes from 'prop-types';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { useDispatch } from 'react-redux';

import { DEFAULT_INACTIVITY_TIMEOUT } from '@src/global/appConstants';
import { useUserInfo } from '@src/hooks/userInfo';
import { refreshAuthToken } from '@src/requests';
import { apiGet, buildApiURL } from '@src/services/api';
import { currentJwt, refreshJwt } from '@src/utils/jwtHelpers';

import { logOut } from '@src/components/helpers';
import MLButton from '@src/components/ui/ml_button';

export const SessionInactivityCheck = (props) => {
    useUserInfo();
    const [showModal, setShowModal] = useState(false);
    const [error, setError] = useState(null);
    const [secondsRemaining, setSecondsRemaining] = useState(DEFAULT_INACTIVITY_TIMEOUT);
    const [logoutTimer, setLogoutTimer] = React.useState(DEFAULT_INACTIVITY_TIMEOUT);
    const dispatch = useDispatch();
    const returnTo = window.location.href;

    useEffect(() => {
        const jwt = currentJwt();
        if (jwt) {
            apiGet(buildApiURL('/api/v1/settings/session_timeouts')).then((response) => {
                const values = response.sessions_setting.setting_values;
                setLogoutTimer(values.logout_timer_seconds);
            }).catch((error) => {
                setLogoutTimer(DEFAULT_INACTIVITY_TIMEOUT);
                // eslint-disable-next-line no-console
                console.error(error);
            });
        }

        const allSecondsRemaining = setInterval(() => {
            const jwt = currentJwt();

            if (!jwt) {
                logOut(returnTo);
                return;
            }

            const decodedJWT = jwtDecode(jwt);
            const expiredAt = new Date(decodedJWT.exp * 1000).getTime();
            const remaining = Math.round((expiredAt - Date.now()) / 1000);

            setSecondsRemaining(remaining);

            if (remaining === null) {
                return;
            }

            if (remaining > logoutTimer) {
                setShowModal(false);
            }

            if (remaining <= 0) {
                logOut(returnTo);
                return;
            }

            if (remaining <= logoutTimer) {
                setShowModal(true);
            }
        }, 1000);

        return () => {
            setShowModal(false);
            clearInterval(allSecondsRemaining);
        };
    }, [logoutTimer, setLogoutTimer, returnTo]);

    const handleContinue = useCallback(() => {
        refreshAuthToken().then((data) => {
            setShowModal(false);
            refreshJwt(data.jwt);
            dispatch({ type: 'USER_INFO_SET' });
        }).catch((error) => {
            setError(error.message);
        });
    }, [setShowModal, setError, dispatch]);

    const handleLogout = useCallback(() => {
        setShowModal(false);
        logOut(returnTo);
    }, [setShowModal, returnTo]);

    return (
        <>
            <Modal
                autoFocus
                centered show={showModal}
                className="logout-modal"
                aria-live="polite"
                aria-atomic="true"
                onHide={handleContinue}
            >
                <Modal.Header closeButton className="logout-modal-header" />
                <Modal.Body className="text-center">
                    <h1 className="modal-title">Inactive Session</h1>
                    <p>Your session is about to expire due to inactivity.</p>
                    <p>You will be logged out in <b aria-atomic="true">{secondsRemaining}</b> seconds</p>

                    { error && <Alert variant="danger">{error}</Alert>}

                    <div className="button-container pt-3 pb-5">
                        <MLButton
                            className="mr-2 mb-2"
                            onClick={handleContinue}
                        >
                            Continue Session
                        </MLButton>
                        <MLButton
                            variant="secondary"
                            onClick={handleLogout}
                        >
                            Log Out
                        </MLButton>
                    </div>
                </Modal.Body>
            </Modal>
            { props.children }
        </>
    );
};

SessionInactivityCheck.propTypes = {
    children: PropTypes.node,
};

export default SessionInactivityCheck;
